import { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button, Tag } from "@carbon/react";
import { Add } from "@carbon/icons-react";
import DataTable, {
  DataTableColumn,
  DataTableRow,
} from "../../components/carbon/DataTable/DataTable";
import PageHeader from "../../components/carbon/PageHeader/PageHeader";
import EmptyState from "../../components/carbon/EmptyState/EmptyState";
import ConfirmDialog from "../../components/carbon/ConfirmDialog/ConfirmDialog";
import {
  SiteListItem,
  usePaginatedSitesByCompany,
  useSetSiteActive,
} from "../../hooks/useSites";
import useDebounce from "../../hooks/useDebounce";
import { useNotification } from "../../context";

const PAGE_SIZE = 10;

interface SiteRow extends DataTableRow {
  id: string;
  internalCode: string;
  name: ReactNode;
  address: string;
  comment: string;
  status: ReactNode;
  actions: ReactNode;
}

// Which activation confirmation is currently open, if any, and for which site — a single
// `ConfirmDialog` instance shared by every row (mirrors User.tsx's `ActivationAction`), rather
// than one dialog per row, since only one row's activation can ever be in flight at a time.
interface PendingActivation {
  site: SiteListItem;
  action: "deactivate" | "reactivate";
}

/**
 * Rebuilt on the Carbon primitives layer (Task 5, Sites flow) on `PageHeader`/`DataTable`/
 * `EmptyState`/`ConfirmDialog`, following `containers/Users/Users.tsx` as the reference
 * list-screen shape — including, since Task 8b, ITS OWN server-side search and pagination.
 * `GET /site/company/{id}` (still `useSites`, kept for seven unmigrated picker consumers — see
 * that hook's own comment) had no query params of its own; `GET /site/company/paginated`
 * (Task 8a) does, so this screen now reads from `usePaginatedSitesByCompany` instead of
 * filtering an in-memory array, exactly the way Users.tsx reads from
 * `usePaginatedUsersByCompany`. The 400ms debounce, the page-0-on-new-search reset, and the
 * 0↔1-indexed `Pagination` boundary below are all copied from that file, not reinvented.
 */
const Sites = () => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();

  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const { debouncedSearchTerm } = useDebounce(searchTerm, 400);
  const [pendingActivation, setPendingActivation] =
    useState<PendingActivation | null>(null);

  // A new search always restarts at the first page.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm]);

  const { data: sitesPage, isLoading } = usePaginatedSitesByCompany(
    page,
    PAGE_SIZE,
    debouncedSearchTerm
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const visibleSites = sitesPage?.content ?? [];

  const { mutate: setSiteActive, isPending: isSettingActive } =
    useSetSiteActive(
      () => {
        openNotification(
          pendingActivation?.action === "deactivate"
            ? t("sites.deactivateSuccess")
            : t("sites.reactivateSuccess")
        );
        setPendingActivation(null);
      },
      () => {
        openNotification(t("newMaterialForm.error"));
        setPendingActivation(null);
      }
    );

  // Genuinely nothing to manage yet, as opposed to a search that merely narrowed the roster to
  // nothing — DataTable's `empty` needs to tell those two apart, see the `empty` node built
  // below. Mirrors Users.tsx's own `isTrulyEmpty` exactly: keyed off the debounced term (there is
  // no unfiltered in-memory array to fall back on any more — the server only ever returns the
  // page the current query actually matches).
  const isTrulyEmpty =
    !isLoading && visibleSites.length === 0 && !debouncedSearchTerm;

  const addAction = (
    <Button as={Link} to="/sites/new" renderIcon={Add}>
      {t("sites.add")}
    </Button>
  );

  const rows: SiteRow[] = useMemo(
    () =>
      visibleSites.map((site) => ({
        id: site.id,
        internalCode: site.internalCode,
        name: <Link to={`/sites/${site.id}`}>{site.name}</Link>,
        address: site.address,
        comment: site.comment || "-",
        // Status as text, not colour alone: the Tag's own label carries the meaning, the colour
        // is a reinforcing cue on top of it — same convention User.tsx uses.
        status: (
          <Tag type={site.active ? "green" : "gray"} size="sm">
            {site.active ? t("cta.active") : t("cta.inactive")}
          </Tag>
        ),
        // The toggle is shown to every role that reaches this screen, unconditionally, and that
        // is deliberate rather than an oversight: PUT /site/{id}/active carries NO role
        // restriction on the backend — only company ownership (SiteController.setActive). Sites
        // have no SiteMutationGuard equivalent, unlike users, so there is no viewer-permission
        // gate to mirror here. If the backend ever grows one, this is the line that must learn
        // about it.
        actions: site.active ? (
          <Button
            kind="danger--ghost"
            size="sm"
            disabled={isSettingActive}
            onClick={() => setPendingActivation({ site, action: "deactivate" })}
          >
            {t("sites.deactivate")}
          </Button>
        ) : (
          <Button
            kind="ghost"
            size="sm"
            disabled={isSettingActive}
            onClick={() => setPendingActivation({ site, action: "reactivate" })}
          >
            {t("sites.reactivate")}
          </Button>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleSites, t, isSettingActive]
  );

  const headers: DataTableColumn<SiteRow>[] = useMemo(
    () => [
      { key: "internalCode", header: t("sites.code") },
      { key: "name", header: t("sites.name") },
      { key: "address", header: t("sites.address") },
      { key: "comment", header: t("sites.comment") },
      { key: "status", header: t("sites.status") },
      { key: "actions", header: "" },
    ],
    [t]
  );

  const empty = debouncedSearchTerm ? (
    <EmptyState
      title={t("sites.noResults")}
      action={
        <Button kind="ghost" onClick={() => setSearchTerm("")}>
          {t("table.clearSearch")}
        </Button>
      }
    />
  ) : (
    <EmptyState title={t("sites.noSites")} action={addAction} />
  );

  return (
    <div>
      <PageHeader
        title={t("sites.title")}
        action={!isTrulyEmpty ? addAction : null}
      />

      <DataTable
        headers={headers}
        rows={rows}
        isLoading={isLoading}
        search={{
          value: searchTerm,
          onChange: setSearchTerm,
          label: t("sites.search"),
        }}
        pagination={{
          // DataTable's Pagination (Carbon) is 1-indexed; this screen's own paging state — and
          // the paginated-sites query it drives — stays 0-indexed to match the API, so the
          // boundary converts both ways. Copied from Users.tsx's identical comment.
          page: page + 1,
          pageSize: PAGE_SIZE,
          totalItems: sitesPage?.totalElements ?? 0,
          onPageChange: (newPage) => setPage(newPage - 1),
        }}
        empty={empty}
      />

      <ConfirmDialog
        open={pendingActivation !== null}
        danger={pendingActivation?.action === "deactivate"}
        title={
          pendingActivation?.action === "deactivate"
            ? t("sites.deactivateTitle")
            : t("sites.reactivateTitle")
        }
        body={
          pendingActivation
            ? t(
                pendingActivation.action === "deactivate"
                  ? "sites.deactivateBody"
                  : "sites.reactivateBody",
                { name: pendingActivation.site.name }
              )
            : ""
        }
        confirmLabel={
          pendingActivation?.action === "deactivate"
            ? t("sites.deactivate")
            : t("sites.reactivate")
        }
        onConfirm={() =>
          pendingActivation &&
          setSiteActive({
            siteId: pendingActivation.site.id,
            active: pendingActivation.action === "reactivate",
          })
        }
        onCancel={() => setPendingActivation(null)}
        isPending={isSettingActive}
      />
    </div>
  );
};

export default Sites;
